// The components the rules document is written against.
//
// This is the reason the rules are MDX rather than markdown: the parts of the
// document that state *numbers* are generated from `RULES` and the recipe
// table, so the page cannot quietly drift out of step with the game the way a
// hand-written list of costs always eventually does. Prose stays prose;
// anything with a number in it asks the rules.
//
// It is also what makes translation cheap later — a translator gets a file of
// sentences, and the tables come out right in every language for free.

import type { ReactNode } from "react";

import { GRADE_NAMES, MINTABLE, mintCost, PIECE_NAMES } from "src/engine/rules/rules";
import { LADDERS, materialName, METAL, mintBill, mintGradeOf } from "src/engine/rules/materials";
import { PALETTE } from "src/client/ai/render/palette";

import { Notice, Pill, styled, Table, TableScroll, Text } from "./kit";

/**
 * The colour a grade is drawn in, matching the ingots on the board.
 *
 * **A material's colour is the board's, not the theme's** — the rule
 * `unitRows.tsx` states — which is why a hex comes out of `PALETTE` here.
 * `undefined` rather than an invented tone where the two lists disagree in
 * length: `GRADE_NAMES` and the palette have to stay the same length, and a
 * document is a poor place to discover they did not.
 */
function gradeColor(grade: number): string | undefined {
  const value = PALETTE.grades[grade];
  return value === undefined ? undefined : `#${value.toString(16).padStart(6, "0")}`;
}

/** The one-paragraph version, set apart at the top. Reads larger than the body. */
const SummaryNotice = styled(Notice)(({ theme }) => ({
  margin: `${theme.kit.space.lg}px 0`,
  fontSize: 16,
  lineHeight: 1.6,
  color: theme.kit.color.cream
}));

/** A rule that surprises people, pulled out of the flow. */
const CalloutNotice = styled(Notice)(({ theme }) => ({
  margin: `${theme.kit.space.lg}px 0`
}));

/** The list of traps, kept visually distinct from the explanation above it. */
const GotchaNotice = styled(Notice)(({ theme }) => ({
  margin: `${theme.kit.space.lg}px 0`,
  "& ul": { margin: 0, paddingLeft: theme.kit.space.lg },
  "& li": { marginBottom: theme.kit.space.sm },
  "& li:last-of-type": { marginBottom: 0 }
}));

/** A ladder of grades, and the arrow into each rung. */
const Ladder = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.kit.space.sm,
  margin: `${theme.kit.space.lg}px 0`
}));

const Step = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.kit.space.sm
}));

const Arrow = styled("span")(({ theme }) => ({ color: theme.kit.color.inkDim }));

const Recipe = styled(TableScroll)(({ theme }) => ({
  margin: `${theme.kit.space.lg}px 0`
}));

export function Summary({ children }: { children?: ReactNode }) {
  return <SummaryNotice tone="info">{children}</SummaryNotice>;
}

export function Callout({ children }: { children?: ReactNode }) {
  return <CalloutNotice tone="info">{children}</CalloutNotice>;
}

/** The metal ladder, in the game's own order and colours. */
export function Grades() {
  return (
    <Ladder>
      {LADDERS[METAL].map((name, grade) => {
        const tone = gradeColor(grade);
        return (
          <Step key={name}>
            {grade > 0 && <Arrow>→</Arrow>}
            <Pill style={tone ? { borderColor: tone, color: tone } : undefined}>
              {name}
            </Pill>
          </Step>
        );
      })}
    </Ladder>
  );
}

/** Every recipe and what it costs, straight from the rules. */
export function Recipes() {
  return (
    <Recipe>
      <Table>
        <thead>
          <tr>
            <th>Piece</th>
            <th>Costs</th>
            <th>Of at least</th>
          </tr>
        </thead>
        <tbody>
          {MINTABLE.map((type) => (
            <tr key={type}>
              <td>
                <Text weight={700}>{PIECE_NAMES[type]}</Text>
              </td>
              <td>
                <Text role="display">{mintCost(type)} units</Text>
                {/* A monarch's other rows (§31 M4): electrum and diamond. */}
                {mintBill(type)
                  .slice(1)
                  .map((row) => (
                    <Text key={`${row.line}-${row.grade}`}>
                      {" "}+ {row.count} {materialName(row.line, row.grade)}
                    </Text>
                  ))}
              </td>
              <td>
                <Text style={{ color: gradeColor(mintGradeOf(type)) }}>
                  {GRADE_NAMES[mintGradeOf(type)]}
                </Text>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Recipe>
  );
}

export function Gotchas({ children }: { children?: ReactNode }) {
  return <GotchaNotice tone="warn">{children}</GotchaNotice>;
}

/** Everything the rules document may use, for the MDX provider. */
export const RULES_COMPONENTS = { Summary, Callout, Grades, Recipes, Gotchas };
